import { popupExperiment } from '@theme/newsletter-popup-experiment';

const configElement = document.getElementById('kokoruno-storefront-analytics-context');
let config;
try {
  config = JSON.parse(configElement?.textContent || 'null');
} catch {
  config = null;
}

if (config?.endpoint && !config.designMode && !popupExperiment.isPreview()) {
  const sessionKey = 'kokoruno_storefront_session_v1';
  const popupSessionKey = 'kokoruno_popup_analytics_session_v1';
  let pageViewId = null;
  const loadedAt = Date.now();
  const queue = [];
  const inFlight = new Map();
  const retryTimers = new Set();
  let active = false;
  let pageObserved = false;
  let hasEverBeenActive = false;
  let lastMarketingAllowed = false;
  let session = null;
  let flushTimer;
  let foregroundSeconds = 0;
  let engagementTimer;

  function validPath(path) {
    return /^\/[^?#@%]{0,299}$/.test(path || '') &&
      !/^\/(?:account|checkouts|orders|apps)(?:\/|$)/i.test(path);
  }

  function safePath() {
    return validPath(location.pathname) ? location.pathname : '/__redacted__';
  }

  function analyticsPermission() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.();
    } catch {
      return undefined;
    }
  }

  function allowed() {
    return analyticsPermission() === true;
  }

  function marketingAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch {
      return false;
    }
  }

  function read(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  }

  function write(key, value) {
    try { sessionStorage.setItem(key, value); } catch {}
  }

  function clearIdentity() {
    popupExperiment.revoke();
    session = null;
    pageViewId = null;
    pageObserved = false;
    lastMarketingAllowed = false;
    foregroundSeconds = 0;
    queue.length = 0;
    clearTimeout(flushTimer);
    clearInterval(engagementTimer);
    for (const timer of retryTimers) clearTimeout(timer);
    retryTimers.clear();
    for (const controller of inFlight.keys()) controller.abort();
    inFlight.clear();
    try {
      sessionStorage.removeItem(sessionKey);
      sessionStorage.removeItem(popupSessionKey);
    } catch {}
  }

  function safeCampaign(value) {
    return /^[\p{L}\p{N} _./:()&+,\-]{1,80}$/u.test(value || '') ? value : undefined;
  }

  function safeChoice(value) {
    return safeCampaign(value) && !/\d{7,}/.test(value) ? value : undefined;
  }

  function acquisition() {
    const result = { landingPath: safePath() };
    if (!marketingAllowed()) return result;
    const params = new URLSearchParams(location.search);
    for (const [param, field] of [['utm_source', 'utmSource'], ['utm_medium', 'utmMedium'], ['utm_campaign', 'utmCampaign']]) {
      const value = safeCampaign(params.get(param)?.trim());
      if (value) result[field] = value;
    }
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin !== location.origin && /^[A-Za-z0-9.-]{1,150}$/.test(referrer.hostname)) {
        result.referrerHost = referrer.hostname;
      }
    } catch {}
    return result;
  }

  function currentSession() {
    const now = Date.now();
    if (!session) {
      try { session = JSON.parse(read(sessionKey) || 'null'); } catch { session = null; }
    }
    if (!session || !/^session_[A-Za-z0-9_-]{8,92}$/.test(session.id || '') ||
        !Number.isFinite(session.startedAt) || !Number.isFinite(session.lastAt) ||
        !validPath(session.acquisition?.landingPath) ||
        session.lastAt < session.startedAt || now < session.lastAt ||
        now - session.lastAt > 30 * 60_000 || now - session.startedAt > 24 * 60 * 60_000) {
      if (queue.length) flush();
      const popupId = !session ? read(popupSessionKey) : null;
      session = {
        id: /^session_[A-Za-z0-9_-]{8,92}$/.test(popupId || '')
          ? popupId : `session_${crypto.randomUUID().replaceAll('-', '')}`,
        startedAt: now,
        lastAt: now,
        acquisition: acquisition(),
      };
      if (pageObserved) {
        pageViewId = `page_${crypto.randomUUID().replaceAll('-', '')}`;
        queue.push({
          ...context(),
          eventId: `event_${crypto.randomUUID().replaceAll('-', '')}`,
          sessionId: session.id,
          acquisition: session.acquisition,
          eventName: 'storefront_page_viewed',
          occurredAt: new Date(now).toISOString(),
          properties: { entryReason: 'session_restart' },
        });
      }
    }
    sanitizeMarketing();
    session.lastAt = now;
    write(sessionKey, JSON.stringify(session));
    write(popupSessionKey, session.id);
    return session;
  }

  function sanitizeMarketing() {
    if (marketingAllowed()) {
      lastMarketingAllowed = true;
      return;
    }
    if (lastMarketingAllowed) {
      const interrupted = [...inFlight.values()];
      for (const controller of inFlight.keys()) controller.abort();
      inFlight.clear();
      lastMarketingAllowed = false;
      for (const batch of interrupted.reverse()) queue.unshift(...batch);
      if (interrupted.length) {
        clearTimeout(flushTimer);
        flushTimer = setTimeout(flush, 0);
      }
    }
    if (session) {
      session.acquisition = { landingPath: session.acquisition?.landingPath || safePath() };
      write(sessionKey, JSON.stringify(session));
    }
    for (const event of queue) {
      if (event.acquisition) event.acquisition = { landingPath: event.acquisition.landingPath };
    }
  }

  function context() {
    const popupExperimentContext = popupExperiment.context();
    const productId = config.productId || undefined;
    const variantId = config.variantId || undefined;
    return {
      schemaVersion: 1,
      pageViewId,
      pageType: config.pageType || 'other',
      pathname: safePath(),
      storefrontLocale: config.locale,
      marketCountry: config.marketCountry,
      viewportCategory: matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      ...(productId ? { productId } : {}),
      ...(variantId ? { variantId } : {}),
      ...(config.family ? { family: config.family } : {}),
      ...(config.cut ? { cut: config.cut } : {}),
      ...(config.packSize > 0 ? { packSize: config.packSize } : {}),
      ...(popupExperimentContext ? { popupExperiment: popupExperimentContext } : {}),
    };
  }

  function emit(eventName, properties, overrides = {}) {
    if (!allowed()) return;
    const current = currentSession();
    const event = {
      ...context(),
      ...overrides,
      eventId: `event_${crypto.randomUUID().replaceAll('-', '')}`,
      sessionId: current.id,
      acquisition: current.acquisition,
      eventName,
      occurredAt: new Date().toISOString(),
      properties,
    };
    const attribution = popupExperiment.prepareAttribution(config.pageType);
    // Queue ordinary analytics immediately. A fast checkout/pagehide must not
    // lose an event while attribution is still waiting for the bounded read.
    enqueue(event);
    if (attribution?.then) {
      const finish = () => {
        if (!active || !allowed() || !queue.includes(event) || currentSession().id !== event.sessionId) return;
        const restored = popupExperiment.context();
        delete event.popupExperiment;
        if (restored && Date.parse(event.occurredAt) >= Date.parse(restored.assignedAt)) event.popupExperiment = restored;
      };
      void attribution.then(finish, finish);
    }
  }

  function enqueue(event) {
    queue.push(event);
    if (queue.length > 50) queue.shift();
    if (queue.length >= 10) flush();
    else {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 2000);
    }
  }

  function flush() {
    clearTimeout(flushTimer);
    if (!allowed()) { clearIdentity(); return; }
    sanitizeMarketing();
    while (queue.length) {
      sendBatch(queue.splice(0, 10));
    }
  }

  function retryBatch(batch, attempt) {
    if (attempt >= 2 || !allowed()) return;
    const timer = setTimeout(() => {
      retryTimers.delete(timer);
      sendBatch(batch, attempt + 1);
    }, attempt === 0 ? 500 : 2000);
    retryTimers.add(timer);
  }

  function sendBatch(batch, attempt = 0) {
    if (!allowed()) return;
    if (!marketingAllowed()) {
      for (const event of batch) {
        if (event.acquisition) event.acquisition = { landingPath: event.acquisition.landingPath };
      }
    }
    const controller = new AbortController();
    inFlight.set(controller, batch);
    try {
      void fetch(config.endpoint, {
        method: 'POST',
        body: JSON.stringify({ events: batch }),
        credentials: 'omit',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        keepalive: true,
        mode: 'cors',
        signal: controller.signal,
      }).then((response) => {
        if (response.status === 429 || response.status >= 500) retryBatch(batch, attempt);
      }).catch(() => {
        if (!controller.signal.aborted) retryBatch(batch, attempt);
      }).finally(() => inFlight.delete(controller));
    } catch {
      inFlight.delete(controller);
      retryBatch(batch, attempt);
    }
  }

  function syncConsent() {
    const permission = analyticsPermission();
    if (permission !== true) {
      // Explicit startup denial must clear identity saved by a previous page.
      // A late/unavailable SDK is unknown permission, not a fresh withdrawal.
      if (active || permission === false) clearIdentity();
      active = false;
      return;
    }
    if (active) {
      sanitizeMarketing();
      return;
    }
    active = true;
    pageViewId = `page_${crypto.randomUUID().replaceAll('-', '')}`;
    foregroundSeconds = 0;
    clearInterval(engagementTimer);
    engagementTimer = setInterval(() => {
      if (!active || document.visibilityState !== 'visible') return;
      foregroundSeconds++;
      if (foregroundSeconds === 10) {
        emit('storefront_page_engaged', { thresholdSeconds: 10 });
        clearInterval(engagementTimer);
      }
    }, 1000);
    emit('storefront_page_viewed', {
      entryReason: hasEverBeenActive || Date.now() - loadedAt > 1000 ? 'consent_granted' : 'navigation',
    });
    pageObserved = true;
    hasEverBeenActive = true;
    if (config.pageType === 'cart') {
      emit('storefront_cart_viewed', { surface: 'page', itemCount: config.cartItemCount || 0 });
    }
    document.dispatchEvent(new Event('kokoruno:analytics-ready'));
  }

  window.kokorunoAnalytics = {
    track(eventName, properties, overrides) {
      syncConsent();
      if (active) emit(eventName, properties, overrides);
    },
    sessionId() {
      syncConsent();
      return active ? currentSession().id : null;
    },
  };

  document.addEventListener('visitorConsentCollected', syncConsent);
  let checks = 0;
  const consentTimer = setInterval(() => {
    syncConsent();
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function' || ++checks >= 20) clearInterval(consentTimer);
  }, 1000);
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], syncConsent);
  } catch {}
  syncConsent();

  const isMainPicker = (target) => config.pageType === 'product' &&
    target?.closest?.('main variant-picker') && !target.closest('product-card, quick-add-dialog');

  document.addEventListener('variant:selected', (event) => {
    if (isMainPicker(event.target)) config.variantId = null;
  });
  document.addEventListener('variant:update', (event) => {
    if (!isMainPicker(event.target)) return;
    const { resource, data } = event.detail;
    if (data.newProduct) config.productId = String(data.newProduct.id);
    else if (String(data.productId) !== String(config.productId)) return;
    config.variantId = resource?.id ? String(resource.id) : null;
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-kokoruno-analytics-option]');
    if (!link || event.defaultPrevented || event.button !== 0) return;
    const optionKind = link.dataset.kokorunoAnalyticsOption;
    const selectedValue = safeChoice(link.dataset.kokorunoAnalyticsValue);
    if (!selectedValue || !['cut', 'pack'].includes(optionKind)) return;
    window.kokorunoAnalytics.track('storefront_product_option_selected', {
      optionKind,
      selectedValue,
      ...(link.dataset.kokorunoTargetProductId ? { targetProductId: link.dataset.kokorunoTargetProductId } : {}),
    });
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.closest?.('main variant-picker')) return;
    if (isMainPicker(input)) config.variantId = null;
    const choice = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
    const optionKind = input.dataset.kokorunoAnalyticsOption || input.closest('[data-kokoruno-analytics-option]')?.dataset.kokorunoAnalyticsOption;
    const selectedValue = safeChoice(choice?.dataset.kokorunoAnalyticsValue);
    if (!selectedValue || !['size', 'color'].includes(optionKind)) return;
    window.kokorunoAnalytics.track('storefront_product_option_selected', {
      optionKind,
      selectedValue,
    });
  }, true);

  document.addEventListener('dialog:open', (event) => {
    if (event.target.matches?.('.product-size-guide__dialog-component')) {
      window.kokorunoAnalytics.track('storefront_size_guide_opened', { surface: 'pdp' });
    } else if (event.target.matches?.('cart-drawer-component')) {
      const itemCount = Number(event.target.querySelector('[ref="cartItemCount"]')?.textContent) || 0;
      window.kokorunoAnalytics.track('storefront_cart_viewed', { surface: 'drawer', itemCount });
    }
  }, true);

  document.addEventListener('kokoruno:cart-add-attempt', (event) => {
    window.kokorunoAnalytics.track('storefront_cart_add_attempted', event.detail.properties, event.detail.context);
  });
  document.addEventListener('kokoruno:cart-add-result', (event) => {
    window.kokorunoAnalytics.track('storefront_cart_add_result', event.detail.properties, event.detail.context);
  });
  document.addEventListener('kokoruno:cart-change-result', (event) => {
    window.kokorunoAnalytics.track('storefront_cart_change_result', event.detail.properties);
  });
  document.addEventListener('kokoruno:cart-gate-state', (event) => {
    window.kokorunoAnalytics.track('storefront_cart_gate_state_changed', event.detail);
  });

  document.addEventListener('submit', (event) => {
    if (event.defaultPrevented || event.submitter?.name !== 'checkout') return;
    const cart = event.target.closest('cart-items-component');
    const itemCount = Number(cart?.querySelector('[ref="cartItemCount"]')?.textContent) || 0;
    if (itemCount < 1) return;
    const checkoutSession = event.target.querySelector('[data-kokoruno-checkout-session]');
    if (checkoutSession) {
      checkoutSession.name = allowed() ? 'attributes[kokoruno_analytics_session_v1]' : '';
      checkoutSession.value = checkoutSession.name ? currentSession().id : '';
    }
    window.kokorunoAnalytics.track('storefront_checkout_handoff', {
      operationId: `operation_${crypto.randomUUID().replaceAll('-', '')}`,
      surface: event.target.closest('cart-drawer-component') ? 'drawer' : 'page',
      itemCount,
    });
  });

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
